import { Info } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Card, Spinner } from "@nutrayan/ui";
import { formatIndianDate } from "@nutrayan/utils";
import { getAdmin, adminLevelLabel } from "@/lib/staff-api";
import { useClaims } from "@/lib/use-claims";
import { roleLabel, tenantLabel } from "@/lib/permissions";
import { StatusBadge } from "@/components/StatusBadge";

function ReadOnlyRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="border-charcoal/5 flex items-center justify-between gap-4 border-b py-2.5 last:border-0">
      <span className="text-body-sm text-muted">{label}</span>
      <span className="text-body-sm text-charcoal text-right font-medium">{children}</span>
    </div>
  );
}

const LOCALE_LABEL: Record<string, string> = { "en-IN": "English", "hi-IN": "हिन्दी (Hindi)" };

export default function ProfilePage() {
  const { userId, email, role, tenantId, locale, adminLevel } = useClaims();

  // The full record comes from the ADMIN_SUPER-gated management endpoint (there is
  // no self-service /admins/me — BLK-API-019). For other roles the fetch 403s and
  // we degrade gracefully to the always-available JWT-claim identity.
  const profile = useQuery({
    queryKey: ["admin", "my-profile", userId],
    queryFn: () => getAdmin(userId!),
    enabled: typeof userId === "number",
    retry: false,
  });

  const displayName = profile.data?.fullName || email?.split("@")[0] || "Administrator";
  const initial = (displayName || "A").slice(0, 1).toUpperCase();
  const status = profile.data?.status;

  return (
    <div className="mx-auto max-w-3xl space-y-6 py-2">
      <div className="flex items-center gap-4">
        <span className="bg-saffron/15 text-saffron font-display flex h-16 w-16 items-center justify-center rounded-full text-2xl">
          {initial}
        </span>
        <div>
          <h1 className="font-display text-display-xl text-charcoal">My profile</h1>
          <p className="text-body-sm text-muted mt-0.5">
            Manage your personal information. Security fields are managed by administrators.
          </p>
        </div>
      </div>

      {/* Account information — read-only */}
      <Card>
        <h2 className="font-display text-heading-md text-charcoal">Account information</h2>
        {profile.isPending && typeof userId === "number" ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : (
          <div className="mt-3">
            <ReadOnlyRow label="Email">{email ?? "—"}</ReadOnlyRow>
            <ReadOnlyRow label="Role">{roleLabel(role)}</ReadOnlyRow>
            <ReadOnlyRow label="Tenant / Organization">{tenantLabel(tenantId)}</ReadOnlyRow>
            <ReadOnlyRow label="User ID">{userId ?? "—"}</ReadOnlyRow>
            {(profile.data?.adminLevel ?? adminLevel) ? (
              <ReadOnlyRow label="Admin level">
                {adminLevelLabel(profile.data?.adminLevel ?? adminLevel)}
              </ReadOnlyRow>
            ) : null}
            {status ? (
              <ReadOnlyRow label="Account status">
                <StatusBadge status={status} />
              </ReadOnlyRow>
            ) : null}
            {profile.data?.passwordStatus ? (
              <ReadOnlyRow label="Password">
                <StatusBadge status={profile.data.passwordStatus} />
              </ReadOnlyRow>
            ) : null}
            {profile.data?.createdAt ? (
              <ReadOnlyRow label="Account created">
                {formatIndianDate(profile.data.createdAt)}
              </ReadOnlyRow>
            ) : null}
          </div>
        )}
      </Card>

      {/* Personal information — read-only (self-service edit is backend-blocked). */}
      <Card>
        <h2 className="font-display text-heading-md text-charcoal">Personal information</h2>
        <div className="mt-3">
          <ReadOnlyRow label="Full name">{profile.data?.fullName || "—"}</ReadOnlyRow>
          <ReadOnlyRow label="Preferred language">
            {LOCALE_LABEL[locale ?? "en-IN"] ?? locale ?? "English"}
          </ReadOnlyRow>
        </div>
        <div className="bg-indigo/10 text-ink mt-4 flex items-start gap-2 rounded-md p-3">
          <Info size={16} className="text-indigo mt-0.5 shrink-0" aria-hidden />
          <p className="text-caption">
            Self-service profile editing isn&apos;t available yet. The backend has no self-profile
            endpoint, and admin records are only editable by a Super Admin via the Staff Registry
            (the bootstrap super admin is immutable). Tracked as a blocker for the backend team
            (BLK-API-019). Contact your administrator to change these details.
          </p>
        </div>
      </Card>
    </div>
  );
}
